import type { BookedSlot } from './bookings'

/**
 * How a schedule shows a private lesson: once, as its booking (D-733).
 *
 * **A private lesson is two rows.** The booking holds the room; a session of
 * the dancer's class holds the attendance, because attendance attaches to a
 * session and never to a booking (D-289, A-1). A screen that lists the
 * sessions and the bookings for a day therefore drew every private twice - a
 * booking card that opened the booking form, and an unlabelled card carrying
 * the dancer's class name that opened her attendance - and *Privates only*
 * hid the second, so the teacher could not reach the lesson's attendance at
 * all with the filter on. Reported by the owner, who asked why the lesson did
 * not simply show on her schedule.
 *
 * **One rule, in one place**, because each screen deciding for itself which of
 * the two rows to show is how privates kept leaking: onto classmates'
 * calendars, onto the class teacher's cover board (D-730), and twice on the
 * teacher's own week. A schedule that draws both a day's sessions and its
 * bookings asks this, and only this.
 */

/**
 * A day's sessions without those a drawn booking already stands for.
 *
 * **Only when the booking is among the ones this reader was given** - the rule
 * removes a duplicate and never the only copy. A private with no booking (made
 * before bookings carried one) keeps its card.
 *
 * **A family never reaches this, and that was the bug** (D-742). This said she
 * was unaffected *because she cannot load bookings*, which is true of her
 * browser and false of the server: `/api/portal/dancer/{id}` adds her privates
 * to the rows it builds, so her schedule drew both halves until it learned the
 * same rule. It applies it server-side, in `PortalEndpoints`; this one is for a
 * screen that draws a day's sessions and its bookings itself.
 */
export function withoutShownPrivates<E extends { bookingId?: string | null }>(
  entries: E[],
  bookings: Pick<BookedSlot, 'id'>[],
): E[] {
  if (bookings.length === 0) return entries
  const shown = new Set(bookings.map((b) => b.id))
  return entries.filter((e) => !(e.bookingId && shown.has(e.bookingId)))
}

/**
 * Whether a schedule shows this booking as off (D-733).
 *
 * **Cancelled, or its lesson called off under it.** A closed day cancels a
 * private's session and leaves its booking standing (D-407). A schedule draws a
 * private as its booking, so a booking read only by its own status showed a
 * lesson nobody was coming to as if it were on - and the struck session beside
 * it, the one honest signal, was the copy `withoutShownPrivates` hides (found by
 * the audit). Every screen that draws a booking asks this, not `status`.
 */
export function bookingIsOff(slot: Pick<BookedSlot, 'status' | 'lessonCalledOff'>): boolean {
  return slot.status === 'Cancelled' || slot.lessonCalledOff === true
}

/** The session a private's card opens, or null when the booking has none (D-733). */
export function lessonSessionOf(slot: Pick<BookedSlot, 'sessionIds'>): string | null {
  return slot.sessionIds?.[0] ?? null
}
